<template>
    <div v-if="modelValue" class="container">
        <div v-if="isLoadingList" class="d-flex align-items-center" style="gap: 5px">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
            <span>{{ $t('loading.inventory') }}</span>
        </div>
        <div v-else class="custom-table-items" @scroll="handleScroll">
            <table class="table">
                <thead class="thead-light">
                    <tr>
                        <th class="table-checkbox" style="width: 25px">
                            <input class="form-check-input" type="checkbox" :checked="selectedRows.length === list.data.length && selectedRows.length > 0" @change="SET_SELECTED_ALL_ROWS()" />
                        </th>
                        <template v-for="column in columns">
                            <th v-if="column.visible" :key="column.key" :style="column.style" @click="column.sortable ? sortByColumn(column.key) : null">
                                <div class="d-flex align-items-center" style="gap: 5px">
                                    {{ $t(`views.inventory.columns.${column.key}`) }}
                                    <font-awesome-icon v-if="sortBy.includes(column.key)" :icon="sortBy.includes('desc') ? ['fas', 'arrow-up-wide-short'] : ['fas', 'arrow-down-wide-short']" class="text-primary" />

                                    <template v-if="column.key === 'cost_of_goods' && bulkCostProcess">
                                        <div v-if="bulkCostStatus.isPending" class="d-flex align-items-center">
                                            <Popper class="light-popper" placement="bottom" hover arrow>
                                                <template #content>
                                                    <div class="d-flex flex-column p-2" style="min-width: 200px">
                                                        <div class="d-flex align-items-center gap-2 mb-2">
                                                            <i class="fas fa-info-circle text-primary"></i>
                                                            <span style="text-transform: none" v-html="$t('views.inventory.bulkCost.pendingTooltip', { count: bulkCostProcess.total_count })"></span>
                                                        </div>
                                                    </div>
                                                </template>
                                                <i style="cursor: auto" class="fas fa-spinner fa-spin text-primary"></i>
                                            </Popper>
                                        </div>
                                        <div v-else-if="bulkCostStatus.isCompleted && bulkCostStatus.hasErrors" @click.stop="showBulkCostErrors">
                                            <Popper class="light-popper" placement="bottom" hover arrow>
                                                <template #content>
                                                    <div class="d-flex flex-column p-2">
                                                        <div style="text-transform: none; font-weight: 500" v-html="$t('views.inventory.bulkCost.successCount', { count: bulkCostProcess.success_count })"></div>
                                                        <div style="text-transform: none; font-weight: 500" v-html="$t('views.inventory.bulkCost.errorCount', { count: bulkCostProcess.error_count })"></div>
                                                    </div>
                                                </template>
                                                <i class="fas fa-exclamation-circle text-danger" style="cursor: pointer"></i>
                                            </Popper>
                                        </div>
                                    </template>
                                </div>
                            </th>
                        </template>
                    </tr>
                </thead>
                <tbody>
                    <tr></tr>
                    <tr v-for="(item, index) in list.data" :key="index" class="bg-white">
                        <td class="table-checkbox" style="width: 25px">
                            <input :value="item.id" :checked="selectedRows.indexOf(item.id) > -1" @change="SET_SELECTED_ROW(item.id)" class="form-check-input" type="checkbox" />
                        </td>
                        <template v-for="column in columns">
                            <td v-if="column.key === 'image' && column.visible" :key="column.key" class="text-center" :style="column.style">
                                <button class="btn btn-link" @click="showVisibleProductDetailDetailModal(item)">
                                    <ImagePlaceholder :img_url="item.product.img_url" />
                                </button>
                            </td>
                            <td v-if="column.key === 'productInfo' && column.visible" :key="column.key" :style="column.style">
                                <div class="d-flex flex-column">
                                    <Popper class="popp" hover arrow>
                                        <template #content>{{ item.product.title }}</template>
                                        <div class="text-ellipsis title">
                                            {{ item.product.title }}
                                        </div>
                                    </Popper>
                                    <div class="d-flex" style="gap: 5px">
                                        <span>ASIN:</span>
                                        <InventoryClipboard :asin="item.identifiers.asin" />
                                    </div>
                                    <div class="d-flex" style="gap: 5px">
                                        <span>SKU:</span>
                                        <InventoryClipboard type="sku" :asin="item.identifiers.asin" :sku="item.identifiers.sku" />
                                    </div>
                                </div>
                            </td>
                            <td v-if="column.key === 'total_quantity' && column.visible" :key="column.key" :style="column.style">
                                <template v-if="+item.inventory?.total_quantity > 0">
                                    <Popper
                                        v-if="item.inventory?.total_quantity > 0"
                                        ref="tooltipButton"
                                        :options="{
                                            modifiers: [
                                                {
                                                    name: 'flip',
                                                    options: {
                                                        fallbackPlacements: ['top', 'right', 'bottom', 'left'],
                                                    },
                                                },
                                                {
                                                    name: 'preventOverflow',
                                                    options: {
                                                        padding: { top: 10, bottom: 10 },
                                                    },
                                                    boundariesElement: 'viewport',
                                                },
                                            ],
                                        }"
                                        placement="bottom"
                                        v-bind="$attrs"
                                        arrow
                                        hover
                                        class="light-popper calculated-price-popper"
                                        style="padding: 5px !important"
                                    >
                                        <template #content>
                                            <div class="popper-table">
                                                <div class="d-flex" style="padding: 5px">
                                                    <div class="d-flex flex-column">
                                                        <div class="c-item" style="padding: 3px 9px !important" v-for="detailKey of Object.keys(item.inventory?.quantity_details).sort()" :key="detailKey">
                                                            {{ $t('views.inventory.quantityDetails.' + detailKey) }}
                                                        </div>
                                                    </div>
                                                    <div class="d-flex flex-column">
                                                        <div
                                                            class="c-item text-secondary"
                                                            style="text-align: right; padding: 3px 9px !important; font-weight: 600"
                                                            v-for="detailKey of Object.keys(item.inventory?.quantity_details).sort()"
                                                            :key="detailKey"
                                                        >
                                                            {{ item.inventory?.quantity_details[detailKey] }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </template>
                                        <span
                                            :class="+item.inventory?.total_quantity_fulfillable === 0 && item.inventory?.total_quantity > 0 && 'text-warning'"
                                            :style="+item.inventory?.total_quantity_fulfillable === 0 && item.inventory?.total_quantity > 0 ? 'text-decoration:underline' : ''"
                                            >{{ item.inventory?.total_quantity_fulfillable.toLocaleString() }}</span
                                        >
                                        <i class="bi bi-info-circle-fill opacity-05" style="margin-left: 2px" :class="+item.inventory?.total_quantity_fulfillable === 0 && item.inventory?.total_quantity > 0 && 'text-warning'"></i>
                                    </Popper>
                                </template>
                                <span v-else class="text-center">0</span>
                            </td>
                            <td v-if="column.key === 'price' && column.visible" :key="column.key">
                                <div class="d-flex justify-content-between" style="gap: 5px">
                                    <strong>{{ item.prices.currency }}</strong>
                                    <strong style="font-variant-numeric: tabular-nums; font-size: 12px">{{ item.prices.price ? formatNumber(item.prices.price) : '-' }}</strong>
                                </div>
                                <div class="d-flex justify-content-between" style="gap: 5px">
                                    <span>Min.</span>
                                    <span style="font-variant-numeric: tabular-nums; font-size: 12px">{{ item.prices.min_price ? formatNumber(item.prices.min_price) : '-' }}</span>
                                </div>
                                <div class="d-flex justify-content-between" style="gap: 5px">
                                    <span>Max.</span>
                                    <span style="font-variant-numeric: tabular-nums; font-size: 12px">{{ item.prices.max_price ? formatNumber(item.prices.max_price) : '-' }}</span>
                                </div>
                            </td>
                            <td v-if="column.key === 'category' && column.visible" :key="column.key">
                                <Popper class="popp" hover arrow>
                                    <template #content>
                                        {{ item.product.category }}
                                    </template>
                                    <div class="text-ellipsis category">{{ item.product.category }}</div>
                                </Popper>
                            </td>
                            <td v-if="column.key === 'brand' && column.visible" :key="column.key">
                                <!-- <span
                            style="
                                padding: 3.67px 6.81px 3.67px 5.82px;
                                border-radius: 800px;
                                background-color: #00203f;
                                font-family: Inter;
                                font-size: 10.5px;
                                font-weight: 700;
                                line-height: 12.71px;
                                text-align: center;
                                color: #fff;
                                margin-right: 5px;
                            "
                            >Satıcı</span
                        > -->{{ item.product?.brand }}
                            </td>
                            <td v-if="column.key === 'bsr' && column.visible" :key="column.key">{{ item.product?.bsr ? item.product?.bsr.toLocaleString() : '-' }}</td>
                            <td v-if="column.key === 'rating' && column.visible" :key="column.key">
                                <star-rating :round-start-rating="false" :star-size="16" :padding="2" pointer="none" :rating="4.5" :showRating="false" :totalRating="100"></star-rating>
                            </td>
                            <td v-if="column.key === 'sales_count' && column.visible" :key="column.key">
                                {{
                                    item.calculated?.sales_count
                                        ? (Math.floor(item.calculated?.sales_count * 100) / 100).toLocaleString(undefined, {
                                              maximumFractionDigits: 2,
                                          })
                                        : '-'
                                }}
                            </td>
                            <td v-if="column.key === 'cost_of_goods' && column.visible" :key="column.key" :style="column.style">
                                <div class="d-flex align-items-center" style="gap: 5px">
                                    <div v-if="item.isCogInputloading" class="spinner-border spinner-border-sm text-primary" role="status"></div>
                                    <span>{{ item.costs.currency }}</span>
                                    <CurrencyInput
                                        v-model="item.costs.cost_of_goods"
                                        :currency="item.costs.currency"
                                        :options="{ valueRange: { min: 0, max: undefined } }"
                                        :disabled="item.expenses_count >= 2 || item.isCogInputloading"
                                        class="form-control form-control-sm"
                                        style="min-height: 14px !important; min-width: 75px; max-width: 75px; padding: 2px 2px 2px 5px !important; font-weight: 600; font-size: 0.8rem"
                                        @focus="() => (storeCOG = item.costs.cost_of_goods)"
                                        @blur="updateCost(item.id, item.costs.cost_of_goods, index)"
                                    />
                                    <button class="btn btn-link p-0" :disabled="item.isCogInputloading" style="line-height: 1" @click="showExpenseModal(item.id, item.identifiers.asin), SET_DETAIL(item)">
                                        <i class="bi bi-plus-square-fill text-primary"></i>
                                    </button>
                                </div>
                            </td>
                            <td v-if="column.key === 'total_fee' && column.visible" :key="column.key">
                                <div v-if="item.fees?.total_fee" class="d-flex" style="gap: 5px">
                                    <span>{{ item.fees.currency }}</span>
                                    <strong>{{ formatNumber(item.fees?.total_fee) }}</strong>
                                </div>
                                <template v-else>-</template>
                            </td>
                            <td v-if="column.key === 'sales_price' && column.visible" :key="column.key">
                                <div v-if="item.calculated?.sales_price" class="d-flex" style="gap: 5px">
                                    <span>{{ item.calculated.currency }}</span>
                                    <strong>{{ formatNumber(item.calculated?.sales_price) }}</strong>
                                </div>
                                <template v-else>-</template>
                            </td>
                            <td v-if="column.key === 'profit_margin' && column.visible" :key="column.key">
                                <strong v-if="item.calculated?.profit_margin" :class="{ 'text-danger': item.calculated?.profit_margin < 0 }">{{ formatPercentage(item.calculated?.profit_margin) }}</strong>
                                <template v-else>-</template>
                            </td>
                            <td v-if="column.key === 'profit' && column.visible" :key="column.key">
                                <template v-if="item.calculated?.profit">
                                    <div class="d-flex" style="gap: 5px">
                                        <span>{{ item.calculated.currency }}</span>
                                        <strong>{{ formatNumber(item.calculated?.profit) }}</strong>
                                    </div>
                                    <div v-if="item.calculated?.roi" class="d-flex align-items-center" :class="[item.calculated?.roi > 0 ? 'text-success' : 'text-danger']" style="gap: 5px">
                                        <i class="fa fa-chart-line"></i>
                                        <span>{{ formatPercentage(item.calculated?.roi) }}</span>
                                    </div>
                                    <div v-if="!item.statuses.is_calculated" class="d-flex align-items-center" style="gap: 5px">
                                        <div class="spinner-border spinner-border-sm" role="status"></div>
                                        <span style="font-size: 12px">{{ $t('views.inventory.calculating') }}</span>
                                    </div>
                                </template>
                                <template v-else>-</template>
                            </td>
                            <td v-if="column.key === 'status' && column.visible" :key="column.key" class="status" :class="item.statuses.status === 1 ? 'active' : 'inactive'">
                                <div class="d-flex flex-column">
                                    <div style="font-weight: 500; color: #00203f">
                                        <span class="legend-indicator" :class="item.statuses.status === 1 ? 'bg-success' : 'bg-danger'"></span
                                        >{{ item.statuses.status === 1 ? $t('views.inventoryStatus.active') : $t('views.inventoryStatus.inactive') }}
                                    </div>
                                    <div v-if="item.statuses.is_buybox">BuyBox</div>
                                    <div v-if="item.statuses.is_lowest">Lowest</div>
                                    <div v-if="item.statuses.is_filtering" class="d-flex align-items-center" style="gap: 5px">
                                        <div class="spinner-border spinner-border-sm" role="status"></div>
                                        <span style="font-size: 12px">{{ $t('statuses.filtering') }}</span>
                                    </div>
                                </div>
                            </td>
                        </template>
                    </tr>
                </tbody>
            </table>
            <!-- BU KISIM GEREKLİ Mİ? -->
            <!-- <div class="d-flex justify-content-end align-items-center" style="margin-right: 40px">
                <div style="margin-right: 8px">
                    <select :value="filterParams.perPage" class="form-select form-select-sm" @change="SET_FILTER_PARAMS({ key: 'perPage', value: +$event.target.value })">
                        <option v-for="i in [10, 25, 50, 100, 250]" :key="i" :value="i">{{ i }} {{ $t('views.inventory.perPage') }}</option>
                    </select>
                </div>
                <div>
                    <select :value="filterParams.page" class="form-select form-select-sm" @change="SET_FILTER_PARAMS({ key: 'page', value: +$event.target.value })">
                        <option v-for="n in totalPages" :key="n" :value="n" :selected="n === filterParams.page">{{ n }}. {{ $t('views.inventory.page') }}</option>
                    </select>
                </div>
            </div> -->
        </div>
    </div>

    <ProductDetailDetailModal v-model="visibleProductDetailDetailModal" />
    <ExpensesModal v-model="visibleExpensesModal" :inventory-id="inventoryId" :asin="inventoryAsin" />
    <BulkCostErrorModal :messages="bulkCostProcess?.messages" :process-id="bulkCostProcess?.id" @dont-show-again="handleBulkCostErrorDontShowAgain" />
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { countryInfo } from '@/utils/helper'
import ExpensesModal from '@/components/Inventory/modals/Expenses'
import ProductDetailDetailModal from '@/components/Inventory/modals/ProductDetail/ProductDetail'
import CurrencyInput from '@/components/CurrencyInput.vue'
import InventoryClipboard from './InventoryClipboard.vue'
import ImagePlaceholder from './ImagePlaceholder.vue'
import { formatPercentage } from '@/utils/format-number'
import BulkCostErrorModal from './modals/BulkCostErrorModal.vue'
import Popper from 'vue3-popper'
import { modalManager } from '@/modal-manager.service'

const countries = countryInfo()

export default {
    name: 'InventoryTableList',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        bulkCostProcess: {
            type: Object,
            default: () => null,
        },
    },
    components: { CurrencyInput, ExpensesModal, ProductDetailDetailModal, InventoryClipboard, ImagePlaceholder, BulkCostErrorModal, Popper },
    computed: {
        ...mapState('inventory', ['columns', 'filterParams', 'list', 'selectedRows', 'isLoadingList']),
        ...mapGetters({
            currentMarket: 'market/currentMarket',
            totalPages: 'inventory/totalPages',
        }),
        getAmazonCountryURL() {
            return countries[this.currentMarket.short].domain
        },
        bulkCostStatus() {
            if (!this.bulkCostProcess) return null
            return {
                isPending: this.bulkCostProcess.status === 'pending',
                isCompleted: this.bulkCostProcess.status === 'completed',
                hasErrors: this.bulkCostProcess.status === 'completed' && this.bulkCostProcess.error_count > 0 && !this.bulkCostProcess.is_checked,
                tooltipText: this.bulkCostProcess.status === 'pending' ? this.$t('views.inventory.bulkCost.pendingTooltip', { count: this.bulkCostProcess.total_count }) : '',
            }
        },
    },
    data() {
        return {
            countries,
            visibleExpensesModal: false,
            visibleProductDetailDetailModal: false,
            showBulkCostErrorModal: false,
            inventoryId: null,
            inventoryAsin: null,
            storeCOG: '',
            sortBy: '',
            formatPercentage,
        }
    },
    mounted() {
        window.addEventListener('scroll', () => {
            const tableEment = document.querySelector('.custom-table-items')
            if (tableEment && window.scrollY > tableEment.offsetTop && window.innerWidth > 768) {
                document.body.classList.add('is-sticky')
            } else {
                document.body.classList.remove('is-sticky')
            }
        })
    },
    unmounted() {
        this.SET_RESET_ALL_ROWS()
        this.SET_FILTER_PARAMS({ key: 'sortBy', value: null })
    },
    methods: {
        ...mapActions('inventory', ['getInventoryList', 'createExpenses']),
        ...mapMutations('inventory', ['SET_SELECTED_ROW', 'SET_SELECTED_ALL_ROWS', 'SET_FILTER_PARAMS', 'SET_RESET_ALL_ROWS', 'SET_DETAIL']),
        formatNumber(number) {
            if (number === null || number === undefined) return null

            const factor = Math.pow(10, 2)
            const rounded = Math.floor(number * factor + 1e-9) / factor
            return rounded.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            })
        },
        showExpenseModal(id, asin) {
            this.visibleExpensesModal = !this.visibleExpensesModal
            this.inventoryId = id
            this.inventoryAsin = asin
        },
        updateCost(inventoryId, value, index) {
            if (this.storeCOG !== value) {
                this.list.data[index].isCogInputloading = true
                const payload = [{ amount: null, price: value, end_date: '' }]
                this.createExpenses({ id: inventoryId, payload }).finally(() => {
                    this.list.data[index].isCogInputloading = false
                })
            }
        },
        showVisibleProductDetailDetailModal(item) {
            this.SET_DETAIL(item)
            this.visibleProductDetailDetailModal = !this.visibleProductDetailDetailModal
        },
        sortByColumn(column) {
            const newSortValue = this.sortBy === `${column}_desc` ? `${column}_asc` : `${column}_desc`
            this.SET_FILTER_PARAMS({ key: 'sortBy', value: newSortValue })
            this.sortBy = newSortValue
            this.getInventoryList()
        },
        handleScroll() {
            document.querySelectorAll('.popper').forEach((element) => {
                element.style.cssText = 'display: none !important'
            })
        },
        async handleBulkCostErrorDontShowAgain(processId) {
            try {
                await this.$emit('check-bulk-cost-process', processId)
                this.showBulkCostErrorModal = false
                this.$emit('update-process-checked', processId)
            } catch (error) {
                console.error('Error handling dont show again:', error)
            }
        },
        showBulkCostErrors() {
            if (this.bulkCostStatus?.isCompleted && this.bulkCostStatus?.hasErrors) {
                this.showBulkCostErrorModal = true
                modalManager.showModal('bulkCostErrorModal')
            }
        },
    },
    watch: {
        visibleExpensesModal: function () {
            if (!this.visibleExpensesModal) {
                this.inventoryId = null
            }
        },
        bulkCostProcess: {
            handler(newVal, oldVal) {
                if (newVal && newVal.status === 'completed' && (!oldVal || oldVal.status !== 'completed')) {
                    setTimeout(() => {
                        this.getInventoryList()
                    }, 500)
                }
            },
            deep: true,
        },
    },
}
</script>
<style lang="scss" scoped>
:root {
    --inventory-gray: #f8fafd;
    --inventory-primary: #ffb53e;
}

body.is-sticky {
    .custom-table-items {
        overflow: visible;
    }
}

.custom-table-items {
    max-width: 100% !important;
    overflow: visible;

    table {
        width: 100%;
        display: table;

        thead {
            top: 0;
            position: sticky;
        }
    }

    tr {
        border-bottom: 1px solid #e7eaf3b2;
        border-left: none !important;
    }
    td {
        vertical-align: middle;
        height: 100px;
        color: #677788 !important;
        font-size: 0.8rem;
        font-weight: 400;
        z-index: 0;
        background-color: #ffffff;

        strong {
            color: #00203f;
            font-weight: 600;
        }

        &.status {
            min-width: 100px;
            text-align: center;

            &.active {
                border-left: 1px solid rgba(22, 204, 162, 0.5);
                background-color: rgba(22, 204, 162, 0.1);
                color: #16cca2;
                text-align: center;
            }

            &.inactive {
                border-left: 1px solid rgba(255, 50, 50, 0.5);
                background-color: rgba(255, 50, 50, 0.1);
                color: #ff3232;
                text-align: center;
            }
        }
    }
}
.thead-light {
    th {
        background-color: var(--inventory-gray) !important;
        border-bottom: 1px solid #e7eaf3b2 !important;
        color: #677788 !important;
        font-size: 11.9px !important;
        font-weight: bold;
        line-height: 17.85px;
        cursor: pointer;
    }

    td {
        height: 58.5px !important;
    }
}
.table-checkbox {
    max-width: 25px;

    input[type='checkbox']:not(:checked) {
        border: 1px solid #677788;
    }
}
.text-ellipsis {
    font-size: 11.9px;
    font-weight: 400;
    line-height: 17.9px;
    color: #677788;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;

    /* &.title {
        max-width: 250px;
    }*/

    &.category {
        max-width: 125px;
    }
}
</style>
